import { BasePathProvider, useSession } from 'components/Providers'
import { useSelectedTeacher } from '../index'
import { Box, Button, Drawer, Grid, Paper, Stack, Typography } from '@mui/material'
import BlockIcon from '@mui/icons-material/Block'
import { useNavigate, useParams } from 'react-router-dom'
import LeaveRequestList from './List'
import LeaveRequestListFilter from './ListFilter.jsx'
import LeaveRequestForm from './Form'
import LeaveRequestPresentation from './RequestPresentation'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { search, searchSelf, initLeaveRequest } from 'store/leave/leave-request'
import { search as searchLeaveRequestType } from 'store/leave/leave-request-type'
import dayjs from 'dayjs'
import { search as searchAcademicYearProvider } from 'store/provider/academic-year'
import { getAcademicYearProviders, getCalendar } from 'store/lookup'
import {
  search as searchRollover,
  save as saveRollover,
  searchSelf as searchRolloverSelf,
  saveSelf as saveRolloverSelf,
  initLeaveRequestRollover,
} from 'store/leave/leave-request-rollover'
import StaffSearch from 'components/StaffSearch'
import PersonSearch from 'components/PersonSearch'

export default function LeaveRequestPage() {
  const navigate = useNavigate()
  const { id } = useParams()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { selectedTeacher: selectedStaff, setSelectedTeacher: setSelectedStaff, mode } = useSelectedTeacher()
  const academicYearsProvider = useSelector(state => state.lookup.academicYearProviders)?.filter(a => a?.isFullYear)
  const [leaveRequestType, setLeaveRequestType] = useState()
  const [academicYear, setAcademicYear] = useState()
  const [eventDates, setEventDates] = useState([])
  const { district } = useSession()
  const leaveRequest = useSelector(state => state.leave.leaveRequest)
  const leaveRequestTypeList = useSelector(state => state.leave.leaveRequestType.list)
  const sessionAcademicYear = useSelector(state => state.session.academicYear)
  const events = useSelector(state => state.lookup.calendar)
  const rolloverInfo = useSelector(state => state.leave.leaveRequestRollover.list)
  const activeAcademicYearProvider = academicYearsProvider?.find(a => a.id === sessionAcademicYear?.activeAcademicYearProviderId)

  const isSummerOrAcademy = district?.type === 'summer' || district?.type === 'academy'

  const basePath = `/leave-request/request`

  useEffect(() => {
    if (isSummerOrAcademy) return
    dispatch(searchAcademicYearProvider())
    const startDate = dayjs(sessionAcademicYear.startDate).format('YYYY-MM-DD')
    const endDate = dayjs(sessionAcademicYear.endDate).format('YYYY-MM-DD')
    dispatch(getCalendar({ startDate, endDate }))
  }, [])

  useEffect(() => {
    if (isSummerOrAcademy) return
    if (!selectedStaff) {
      dispatch(initLeaveRequestRollover())
      return
    }
    createRollover()
  }, [sessionAcademicYear.id, selectedStaff])

  useEffect(() => {
    if (isSummerOrAcademy) return
    if (!academicYearsProvider) dispatch(getAcademicYearProviders())
  }, [sessionAcademicYear.id])

  useEffect(() => {
    if (isSummerOrAcademy) return
    if (activeAcademicYearProvider?.id) setAcademicYear(activeAcademicYearProvider.id)
  }, [activeAcademicYearProvider?.id])

  useEffect(() => {
    if (isSummerOrAcademy) return
    if (!leaveRequestTypeList) dispatch(searchLeaveRequestType())
  }, [sessionAcademicYear.id])

  useEffect(() => {
    if (isSummerOrAcademy) return
    if (!selectedStaff) {
      dispatch(initLeaveRequest())
      return
    }

    const payload = {
      ...(mode !== 'self' && { personId: selectedStaff.personId }),
      limit: leaveRequest.query?.limit || 10,
      offset: leaveRequest.query?.offset || 0,
      ...(leaveRequestType && { typeId: leaveRequestType }),
      ...(academicYear && { academicYearProviderId: academicYear }),
      ...(leaveRequest.query?.sortBy && { sortBy: leaveRequest.query.sortBy }),
      ...(leaveRequest.query?.sortOrder && { sortOrder: leaveRequest.query.sortOrder }),
      ...(academicYear && { academicYearProviderId: academicYear }),
    }

    const searchAction = mode === 'self' ? searchSelf : search
    dispatch(searchAction(payload))
    getEventDates()
  }, [
    sessionAcademicYear.id,
    selectedStaff?.personId,
    leaveRequestType,
    academicYear,
    leaveRequest.query?.limit,
    leaveRequest.query?.offset,
    leaveRequest.query?.sortBy,
    leaveRequest.query?.sortOrder,
  ])

  function getEventDates() {
    if (!events) return
    const dates = []
    events.forEach(event => {
      if (!event.isSchoolDay) {
        for (
          let day = dayjs(event.startDate);
          day.isSame(event.endDate, 'day') || day.isBefore(event.endDate, 'day');
          day = day.add(1, 'day')
        ) {
          dates.push({ date: day.format('YYYY-MM-DD') })
        }
      }
    })
    setEventDates(dates)
  }

  function createRollover() {
    if (!selectedStaff || !sessionAcademicYear) return
    const rolloverSearch = mode === 'self' ? searchRolloverSelf : searchRollover
    const rolloverSave = mode === 'self' ? saveRolloverSelf : saveRollover
    const rolloverPayload = {
      ...(mode !== 'self' && { personId: selectedStaff.personId }),
    }
    dispatch(rolloverSearch(rolloverPayload)).then(result => {
      if (!result.payload || result.payload.count === 0) {
        dispatch(
          rolloverSave({
            ...rolloverPayload,
            rolloverSickDays: 0,
            proratedSickDays: 10,
            usedSickDays: 0,
            rolloverPersonalDays: 0,
            proratedPersonalDays: 2,
            usedPersonalDays: 0,
            rolloverWellnessDays: 0,
            proratedWellnessDays: 1,
            usedWellnessDays: 0,
            rolloverVacationDays: 0,
            proratedVacationDays: 16,
            usedVacationDays: 0,
            usedUnpaidDays: 0,
          }),
        )
      }
    })
  }

  if (isSummerOrAcademy) return (
    <Paper
      elevation={0}
      sx={{
        my: 2,
        p: 2.5,
        border: '1px solid',
        borderColor: 'warning.light',
        borderLeft: '5px solid',
        borderLeftColor: 'warning.main',
        borderRadius: 2,
        bgcolor: 'warning.50',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <BlockIcon sx={{ color: 'warning.main', fontSize: 32, flexShrink: 0 }} />
      <Box>
        <Typography variant="subtitle1" fontWeight={700} color="warning.dark">
          {t('Leave Requests Unavailable')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {t(`Leave requests cannot be submitted for ${district?.type} campuses. Please connect to your school account to submit leave requests.`)}
        </Typography>
      </Box>
    </Paper>
  )

  return (
    <BasePathProvider value={`${basePath}`}>
      <Grid container spacing={2} sx={{ mt: 2 }}>
        {mode === 'hr' && (
          <Grid item md={4} xs={12}>
            <PersonSearch
              label={t('Staff Member')}
              value={selectedStaff?.person ?? null}
              onChange={p => setSelectedStaff(p ? { personId: p.id, person: p } : null)}
            />
          </Grid>
        )}
        {mode === 'admin' && (
          <Grid item md={4} xs={12}>
            <StaffSearch label={t('Staff Member')} onSelect={setSelectedStaff} value={selectedStaff} />
          </Grid>
        )}
        {mode !== 'self' && <Grid item md={4} xs={12} />}
        <Grid item md={mode === 'self' ? 12 : 4} xs={12}>
          <Stack justifyContent="flex-end" flexDirection="row" sx={{ mr: 0.5, mb: 3 }}>
            <Button
              color="primary"
              size="small"
              variant="contained"
              disabled={!selectedStaff}
              sx={{ mr: 2 }}
              onClick={() => navigate(`${basePath}/new`)}
            >
              {t('NEW REQUEST')}
            </Button>
          </Stack>
        </Grid>
      </Grid>

      {rolloverInfo ? <LeaveRequestPresentation rollover={rolloverInfo} /> : null}
      <Typography sx={{ textAlign: 'left', mt: '25px', fontSize: '20px' }}>{t('Submitted Requests')}</Typography>
      <LeaveRequestListFilter
        leaveRequestTypeList={leaveRequestTypeList}
        academicYearsProvider={academicYearsProvider}
        selectedTeacher={selectedStaff}
        leaveRequestType={leaveRequestType}
        setLeaveRequestType={setLeaveRequestType}
        academicYear={academicYear}
        setAcademicYear={setAcademicYear}
      />
      <LeaveRequestList
        leaveRequestList={leaveRequest.list}
        selectedTeacher={selectedStaff}
        academicYear={academicYear}
        leaveRequestType={leaveRequestType}
      />
      <Drawer
        PaperProps={{ sx: { width: 800 } }}
        open={Boolean(id)}
        anchor="right"
        onClose={() => navigate(basePath)}
      >
        <LeaveRequestForm
          leaveRequestTypeList={leaveRequestTypeList}
          selectedTeacher={selectedStaff}
          events={eventDates}
          mode={mode}
        />
      </Drawer>
    </BasePathProvider>
  )
}
